import { canAccessCrm } from "../../utils/crmAccess";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { createTheme as createMuiTheme, ThemeProvider as MuiThemeProvider, useTheme as useMuiTheme } from "@mui/material/styles";
import { getCrmColors, getCrmCardColor } from "../../theme/crmColors";
import CallIcon from "@material-ui/icons/Call";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import MessageIcon from "@material-ui/icons/Message";
import SendIcon from "@material-ui/icons/Send";
import AccessAlarmIcon from "@material-ui/icons/AccessAlarm";
import TimerIcon from "@material-ui/icons/Timer";
import QueueIcon from "@material-ui/icons/LowPriority";
import StarIcon from "@material-ui/icons/Star";
import InboxIcon from "@material-ui/icons/MoveToInbox";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import * as XLSX from "xlsx";
import moment from "moment";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { Refresh, SaveAlt } from "@mui/icons-material";

import TabPanel from "../../components/TabPanel";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import { ChatsUser } from "./ChartsUser";
import { ChartsDate } from "./ChartsDate";
import { i18n } from "../../translate/i18n";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "calc(100vh - 64px)",
    padding: theme.spacing(3),
    backgroundColor: ({ surface }) => surface.page,
    color: ({ surface }) => surface.text,
    colorScheme: ({ surface }) => surface.mode,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2)
    }
  },
  tabPanel: {
    width: "100%",
    paddingTop: theme.spacing(2)
  },
  tableExportArea: {
    width: "100%"
  }
}));

const cchatBlue = "#005AE5";
const cchatSoftBlue = "#5daafc";

const numberFormatter = new Intl.NumberFormat("pt-BR");

const getNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value) => numberFormatter.format(getNumber(value));

const formatPercent = (value) => `${Number(getNumber(value).toFixed(2)).toLocaleString("pt-BR")}%`;

const formatTime = (minutes) => {
  const total = Math.max(Math.round(getNumber(minutes)), 0);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${String(hours).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
};

const getSurface = (isDark) => ({
  page: isDark ? "#424242" : "#f4f7fb",
  panel: isDark ? "#4a4a4a" : "#ffffff",
  panelAlt: isDark ? "#505050" : "#f8fbff",
  border: isDark ? "rgba(255,255,255,0.10)" : "#dbe4f0",
  text: isDark ? "#f8fafc" : "#111827",
  muted: isDark ? "#cbd5e1" : "#64748b",
  subtle: isDark ? "#3f3f3f" : "#eef4ff"
});

const NpsSurfaceContext = React.createContext(null);
const useSurface = isDark => useContext(NpsSurfaceContext) || getSurface(isDark);

const iconWrapSx = (color, isDark) => ({
  width: 42,
  height: 42,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color,
  backgroundColor: isDark ? "rgba(255,255,255,0.08)" : `${color}18`
});

const MetricCard = ({ title, value, helper, icon, color, isDark }) => {
  const surface = useSurface(isDark);

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: 2,
        borderRadius: "8px",
        border: `1px solid ${surface.border}`,
        backgroundColor: surface.card || surface.panel,
        boxShadow: isDark ? "0 10px 24px rgba(0,0,0,0.12)" : "0 8px 18px rgba(15,23,42,0.04)"
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: surface.muted,
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase"
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: surface.text,
              fontSize: { xs: 26, md: 30 },
              fontWeight: 800,
              lineHeight: 1.15,
              wordBreak: "break-word"
            }}
          >
            {value}
          </Typography>
          {helper ? (
            <Typography sx={{ mt: 0.75, color: surface.muted, fontSize: 12 }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
        <Box sx={iconWrapSx(color, isDark)}>{icon}</Box>
      </Stack>
    </Paper>
  );
};

const OperationItem = ({ label, value, helper, color, isDark }) => {
  const surface = useSurface(isDark);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "8px",
        border: `1px solid ${surface.border}`,
        backgroundColor: surface.panel,
        height: "100%"
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: surface.text, fontWeight: 700, fontSize: 14 }}>{label}</Typography>
          <Typography sx={{ color: surface.muted, fontSize: 12 }}>{helper}</Typography>
        </Box>
        <Typography sx={{ color: surface.text, fontWeight: 800, fontSize: 24 }}>{value}</Typography>
      </Stack>
    </Paper>
  );
};

const NpsDonut = ({ label, value, count, color, isDark }) => {
  const surface = useSurface(isDark);
  const safeValue = Math.min(Math.max(getNumber(value), 0), 100);
  const size = 112;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (safeValue / 100) * circumference;

  return (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{
        py: 1,
        px: 1,
        minHeight: 184,
        justifyContent: "center"
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color }} />
        <Typography sx={{ color: surface.text, fontWeight: 800, fontSize: 13 }}>
          {label}
        </Typography>
      </Stack>
      <Box
        sx={{
          position: "relative",
          width: size,
          height: size,
          display: "grid",
          placeItems: "center"
        }}
        aria-label={`${label}: ${formatPercent(safeValue)}`}
      >
        <Box
          component="svg"
          viewBox={`0 0 ${size} ${size}`}
          sx={{ width: size, height: size, transform: "rotate(-90deg)", overflow: "visible" }}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={surface.track || (isDark ? "rgba(255,255,255,0.12)" : "#e8eef7")}
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference}`}
            sx={{
              filter: isDark ? "drop-shadow(0 0 5px rgba(0,0,0,0.25))" : "drop-shadow(0 3px 6px rgba(15,23,42,0.08))",
              transition: "stroke-dasharray 260ms ease"
            }}
          />
        </Box>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        >
          <Typography sx={{ color: surface.text, fontWeight: 900, fontSize: 22, lineHeight: 1 }}>
            {formatPercent(safeValue)}
          </Typography>
          <Typography sx={{ color: surface.muted, fontWeight: 700, fontSize: 11, mt: 0.5 }}>
            do total
          </Typography>
        </Stack>
      </Box>
      <Typography sx={{ color: surface.muted, fontWeight: 700, fontSize: 12 }}>
        {formatNumber(count)} avaliacoes
      </Typography>
    </Stack>
  );
};

const EmptyState = ({ isDark }) => {
  const surface = useSurface(isDark);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "8px",
        border: `1px dashed ${surface.border}`,
        backgroundColor: surface.panel,
        textAlign: "center"
      }}
    >
      <Box sx={{ ...iconWrapSx(surface.accent || cchatBlue, isDark), mx: "auto", mb: 1.5 }}>
        <StarIcon fontSize="small" />
      </Box>
      <Typography sx={{ color: surface.text, fontWeight: 800 }}>Sem avaliacoes no periodo</Typography>
      <Typography sx={{ color: surface.muted, mt: 0.5, fontSize: 13 }}>
        Quando um atendimento for finalizado com avaliacao, os dados aparecem aqui.
      </Typography>
    </Paper>
  );
};

const Dashboard = () => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isDark = (theme.palette?.type || theme.mode) === "dark";
  const surface = useSurface(isDark);
  const classes = useStyles({ surface });
  const { find } = useDashboard();
  const { user } = useContext(AuthContext);
  const crmAllowed = canAccessCrm(user);

  const now = moment().format("YYYY-MM-DD");
  const firstDay = moment().startOf("month").format("YYYY-MM-DD");

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [dateStartTicket, setDateStartTicket] = useState(firstDay);
  const [dateEndTicket, setDateEndTicket] = useState(now);
  const [loading, setLoading] = useState(false);
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const tab = ["Indicadores", ...(crmAllowed ? ["NPS"] : []), "Atendentes"].includes(requestedTab) ? requestedTab : "Indicadores";

  const onlineAttendants = useMemo(
    () => attendants.filter((attendant) => attendant.online === true).length,
    [attendants]
  );

  const periodLabel = `${moment(dateStartTicket).format("DD/MM/YYYY")} ate ${moment(dateEndTicket).format("DD/MM/YYYY")}`;

  async function fetchData() {
    if (!moment(dateStartTicket).isValid() || !moment(dateEndTicket).isValid()) {
      toast.error("Informe um periodo valido");
      return;
    }

    setLoading(true);

    try {
      const data = await find({
        date_from: moment(dateStartTicket).format("YYYY-MM-DD"),
        date_to: moment(dateEndTicket).format("YYYY-MM-DD")
      });

      setCounters(data?.counters || {});
      setAttendants(Array.isArray(data?.attendants) ? data.attendants : []);
    } catch (error) {
      toast.error("Erro ao carregar os dados do dashboard");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportarGridParaExcel = () => {
    const table = document.getElementById("grid-attendants");

    if (!table) {
      toast.warning("Nao ha dados de atendentes para exportar");
      return;
    }

    const ws = XLSX.utils.table_to_sheet(table);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RelatorioDeAtendentes");
    XLSX.writeFile(wb, "relatorio-de-atendentes.xlsx");
  };

  const handleChangeTab = (e, newValue) => {
    const search = new URLSearchParams(location.search);
    search.set("tab", newValue);
    history.replace({ pathname: location.pathname, search: search.toString() });
  };

  const kpiCards = [
    {
      title: i18n.t("dashboard.cards.inAttendance"),
      value: formatNumber(counters.supportHappening),
      helper: "Conversas abertas agora",
      icon: <CallIcon fontSize="small" />,
      color: cchatBlue
    },
    {
      title: i18n.t("dashboard.cards.waiting"),
      value: formatNumber(counters.supportPending),
      helper: "Aguardando atendimento",
      icon: <HourglassEmptyIcon fontSize="small" />,
      color: "#f59e0b"
    },
    {
      title: i18n.t("dashboard.cards.finalized"),
      value: formatNumber(counters.supportFinished),
      helper: "Fechados no periodo",
      icon: <CheckCircleIcon fontSize="small" />,
      color: "#25D366"
    },
    {
      title: i18n.t("dashboard.cards.newContacts"),
      value: formatNumber(counters.leads),
      helper: "Contatos criados no periodo",
      icon: <GroupAddIcon fontSize="small" />,
      color: "#7c3aed"
    },
    {
      title: i18n.t("dashboard.cards.totalReceivedMessages"),
      value: `${formatNumber(counters.receivedMessagesPeriod)} / ${formatNumber(counters.totalReceivedMessages)}`,
      helper: "Periodo / total",
      icon: <MessageIcon fontSize="small" />,
      color: "#0ea5e9"
    },
    {
      title: i18n.t("dashboard.cards.totalSentMessages"),
      value: `${formatNumber(counters.sentMessagesPeriod)} / ${formatNumber(counters.totalSentMessages)}`,
      helper: "Periodo / total",
      icon: <SendIcon fontSize="small" />,
      color: "#16a34a"
    },
    {
      title: i18n.t("dashboard.cards.averageServiceTime"),
      value: formatTime(counters.avgSupportTime),
      helper: "Tempo medio de atendimento",
      icon: <AccessAlarmIcon fontSize="small" />,
      color: "#fb923c"
    },
    {
      title: i18n.t("dashboard.cards.averageWaitingTime"),
      value: formatTime(counters.avgWaitTime),
      helper: "Tempo medio em fila",
      icon: <TimerIcon fontSize="small" />,
      color: "#ef4444"
    }
  ];

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  return (
    <Box className={classes.page}>
      <Container maxWidth={false} disableGutters>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2,
            borderRadius: "8px",
            border: `1px solid ${surface.border}`,
            backgroundColor: surface.panel,
            boxShadow: isDark ? "0 12px 28px rgba(0,0,0,0.14)" : "0 10px 24px rgba(15,23,42,0.05)"
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography sx={{ color: surface.text, fontWeight: 900, fontSize: 24 }}>
                  Dashboard
                </Typography>
                <Chip
                  size="small"
                  label={periodLabel}
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    color: surface.accent || (isDark ? "#dbeafe" : cchatBlue),
                    backgroundColor: surface.accentSoft || (isDark ? "rgba(93,170,252,0.18)" : "#e8f2ff")
                  }}
                />
              </Stack>
              <Typography sx={{ color: surface.muted, fontSize: 14 }}>
                Visao operacional dos atendimentos, mensagens e avaliacoes do Cchat.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }}>
              <TextField
                label={i18n.t("dashboard.date.initialDate")}
                type="date"
                size="small"
                value={dateStartTicket}
                onChange={(event) => setDateStartTicket(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: surface.panelAlt,
                    color: surface.text,
                    borderRadius: "8px"
                  },
                  "& .MuiInputLabel-root": { color: surface.muted },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: surface.border }
                }}
              />
              <TextField
                label={i18n.t("dashboard.date.finalDate")}
                type="date"
                size="small"
                value={dateEndTicket}
                onChange={(event) => setDateEndTicket(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: surface.panelAlt,
                    color: surface.text,
                    borderRadius: "8px"
                  },
                  "& .MuiInputLabel-root": { color: surface.muted },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: surface.border }
                }}
              />
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                onClick={fetchData}
                disabled={loading}
                sx={{
                  minHeight: 40,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 800,
                  backgroundColor: surface.accent || cchatBlue,
                  color: surface.buttonText,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: surface.buttonHover || "#0048b8", boxShadow: "none" }
                }}
              >
                Atualizar
              </Button>
              <Tooltip title="Exportar tabela de atendentes">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<SaveAlt />}
                    onClick={exportarGridParaExcel}
                    sx={{
                      minHeight: 40,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 800,
                      color: surface.accent || (isDark ? "#dbeafe" : cchatBlue),
                      borderColor: surface.accent || (isDark ? "rgba(219,234,254,0.28)" : "#bfdbfe"),
                      "&:hover": {
                        borderColor: surface.accent || cchatSoftBlue,
                        backgroundColor: surface.accentSoft || (isDark ? "rgba(93,170,252,0.12)" : "#eef6ff")
                      }
                    }}
                  >
                    Exportar
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            minHeight: 42,
            borderBottom: `1px solid ${surface.border}`,
            "& .MuiTab-root": {
              minHeight: 42,
              px: 2,
              textTransform: "none",
              fontWeight: 800,
              color: surface.muted
            },
            "& .Mui-selected": {
              color: surface.accent || (isDark ? "#dbeafe" : cchatBlue)
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "8px 8px 0 0",
              backgroundColor: surface.accent || cchatBlue
            }
          }}
        >
          <Tab value="Indicadores" label={i18n.t("dashboard.tabs.indicators")} />
          {crmAllowed && <Tab value="NPS" label={i18n.t("dashboard.tabs.assessments")} />}
          <Tab value="Atendentes" label={i18n.t("dashboard.tabs.attendants")} />
        </Tabs>

        <TabPanel className={classes.tabPanel} value={tab} name="Indicadores">
          <Grid2 container spacing={2}>
            {kpiCards.map((card) => (
              <Grid2 xs={12} sm={6} lg={3} key={card.title}>
                <MetricCard {...card} isDark={isDark} />
              </Grid2>
            ))}
          </Grid2>

          <Box sx={{ mt: 3, mb: 1.5 }}>
            <Typography sx={{ color: surface.text, fontWeight: 900, fontSize: 18 }}>
              Operacao agora
            </Typography>
            <Typography sx={{ color: surface.muted, fontSize: 13 }}>
              Leitura rapida do que esta exigindo atencao neste momento.
            </Typography>
          </Box>

          <Grid2 container spacing={2}>
            <Grid2 xs={12} md={3}>
              <OperationItem
                label="Em atendimento"
                helper="Tickets abertos"
                value={formatNumber(counters.supportHappening)}
                color={surface.accent || cchatBlue}
                isDark={isDark}
              />
            </Grid2>
            <Grid2 xs={12} md={3}>
              <OperationItem
                label="Aguardando"
                helper="Clientes esperando"
                value={formatNumber(counters.supportPending)}
                color="#f59e0b"
                isDark={isDark}
              />
            </Grid2>
            <Grid2 xs={12} md={3}>
              <OperationItem
                label="Sem fila"
                helper="Conversas sem roteamento"
                value={formatNumber(counters.withoutQueueNow)}
                color="#ef4444"
                isDark={isDark}
              />
            </Grid2>
            {crmAllowed && <Grid2 xs={12} md={3}>
              <OperationItem
                label="Aguardando avaliacao"
                helper="Tickets em NPS"
                value={formatNumber(counters.waitingRatingNow)}
                color="#8b5cf6"
                isDark={isDark}
              />
            </Grid2>}
          </Grid2>
        </TabPanel>

        {crmAllowed && <TabPanel className={classes.tabPanel} value={tab} name="NPS">
          {getNumber(counters.withRating) === 0 ? (
            <EmptyState isDark={isDark} />
          ) : (
            <Grid2 container spacing={2}>
              <Grid2 xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 3,
                    borderRadius: "8px",
                    border: `1px solid ${surface.border}`,
                    backgroundColor: surface.panel
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Box sx={iconWrapSx(surface.accent || cchatBlue, isDark)}>
                      <TrendingUpIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography sx={{ color: surface.text, fontWeight: 900 }}>Score NPS</Typography>
                      <Typography sx={{ color: surface.muted, fontSize: 12 }}>
                        Escala {counters.npsScale || "1-5"}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography sx={{ color: surface.text, fontSize: 56, fontWeight: 900, lineHeight: 1 }}>
                    {formatPercent(counters.npsScore)}
                  </Typography>
                  <Typography sx={{ color: surface.muted, mt: 1, fontSize: 13 }}>
                    Positivos menos negativos dentro do periodo selecionado.
                  </Typography>
                </Paper>
              </Grid2>

              <Grid2 xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 3,
                    borderRadius: "8px",
                    border: `1px solid ${surface.border}`,
                    backgroundColor: surface.panel
                  }}
                >
                  <Typography sx={{ color: surface.text, fontWeight: 900, mb: 2 }}>
                    Distribuicao das avaliacoes
                  </Typography>
                  <Grid2 container spacing={2}>
                    <Grid2 xs={12} sm={4}>
                      <NpsDonut
                        label="Positivos"
                        value={counters.npsPromotersPerc}
                        count={counters.npsPromoters}
                        color="#25D366"
                        isDark={isDark}
                      />
                    </Grid2>
                    <Grid2 xs={12} sm={4}>
                      <NpsDonut
                        label="Neutros"
                        value={counters.npsPassivePerc}
                        count={counters.npsPassive}
                        color="#facc15"
                        isDark={isDark}
                      />
                    </Grid2>
                    <Grid2 xs={12} sm={4}>
                      <NpsDonut
                        label="Negativos"
                        value={counters.npsDetractorsPerc}
                        count={counters.npsDetractors}
                        color="#ef4444"
                        isDark={isDark}
                      />
                    </Grid2>
                  </Grid2>
                </Paper>
              </Grid2>

              <Grid2 xs={12} sm={6} md={3}>
                <MetricCard
                  title={i18n.t("dashboard.assessments.ratedCalls")}
                  value={formatNumber(counters.withRating)}
                  helper="Com nota recebida"
                  icon={<StarIcon fontSize="small" />}
                  color="#25D366"
                  isDark={isDark}
                />
              </Grid2>
              <Grid2 xs={12} sm={6} md={3}>
                <MetricCard
                  title={i18n.t("dashboard.assessments.callsWaitRating")}
                  value={formatNumber(counters.waitRating)}
                  helper="Aguardando resposta"
                  icon={<InboxIcon fontSize="small" />}
                  color="#8b5cf6"
                  isDark={isDark}
                />
              </Grid2>
              <Grid2 xs={12} sm={6} md={3}>
                <MetricCard
                  title={i18n.t("dashboard.assessments.callsWithoutRating")}
                  value={formatNumber(counters.withoutRating)}
                  helper="Fechados sem nota"
                  icon={<QueueIcon fontSize="small" />}
                  color="#ef4444"
                  isDark={isDark}
                />
              </Grid2>
              <Grid2 xs={12} sm={6} md={3}>
                <MetricCard
                  title={i18n.t("dashboard.assessments.evaluationIndex")}
                  value={formatPercent(counters.percRating)}
                  helper={`${formatNumber(counters.withRating)} de ${formatNumber(counters.tickets)} tickets`}
                  icon={<CheckCircleIcon fontSize="small" />}
                  color={surface.accent || cchatBlue}
                  isDark={isDark}
                />
              </Grid2>
            </Grid2>
          )}
        </TabPanel>}

        <TabPanel className={classes.tabPanel} value={tab} name="Atendentes">
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
            <Box>
              <Typography sx={{ color: surface.text, fontWeight: 900, fontSize: 18 }}>
                Atendentes
              </Typography>
              <Typography sx={{ color: surface.muted, fontSize: 13 }}>
                {onlineAttendants} online de {attendants.length} cadastrados.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<SaveAlt />}
              onClick={exportarGridParaExcel}
              sx={{
                alignSelf: { xs: "stretch", sm: "center" },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 800,
                backgroundColor: surface.accent || cchatBlue,
                  color: surface.buttonText,
                boxShadow: "none",
                "&:hover": { backgroundColor: surface.buttonHover || "#0048b8", boxShadow: "none" }
              }}
            >
              Exportar Excel
            </Button>
          </Stack>

          <Box id="grid-attendants" className={classes.tableExportArea}>
            <TableAttendantsStatus attendants={attendants} loading={loading} showRatings={crmAllowed} />
          </Box>

          {crmAllowed && <>
          <Divider sx={{ my: 2.5, borderColor: surface.border }} />

          <Grid2 container spacing={2}>
            <Grid2 xs={12} lg={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  border: `1px solid ${surface.border}`,
                  backgroundColor: surface.panel,
                  overflow: "hidden"
                }}
              >
                <ChatsUser />
              </Paper>
            </Grid2>
            <Grid2 xs={12} lg={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  border: `1px solid ${surface.border}`,
                  backgroundColor: surface.panel,
                  overflow: "hidden"
                }}
              >
                <ChartsDate />
              </Paper>
            </Grid2>
          </Grid2>
          </>}
        </TabPanel>
      </Container>
    </Box>
  );
};

// Keep the dashboard instance mounted across tabs so dates and loaded data survive.
// NPS uses MUI v5 controls; the outer app theme is MUI v4.
const DashboardWithPalette = () => {
  const outerTheme = useTheme();
  const inheritedMuiTheme = useMuiTheme();
  const location = useLocation();
  const isNps = new URLSearchParams(location.search).get("tab") === "NPS";
  const surface = useMemo(() => {
    if (!isNps) return null;
    const colors = getCrmColors(outerTheme);
    const mode = outerTheme.palette?.type || outerTheme.mode || "light";
    return {
      mode, page: colors.background, panel: colors.surface, panelAlt: colors.inset,
      card: getCrmCardColor(outerTheme), border: colors.border, text: colors.text,
      muted: colors.muted, track: colors.border, accent: colors.accent,
      accentSoft: colors.accentSoft, buttonHover: colors.focus,
      buttonText: mode === "dark" ? colors.background : "#FFFFFF"
    };
  }, [isNps, outerTheme]);
  const muiTheme = useMemo(() => {
    if (!surface) return inheritedMuiTheme;
    const colors = getCrmColors(outerTheme);
    return createMuiTheme({
      palette: {
        mode: surface.mode,
        primary: { main: colors.accent, contrastText: surface.buttonText },
        background: { default: colors.background, paper: colors.surface },
        text: { primary: colors.text, secondary: colors.muted },
        divider: colors.border,
        action: { active: colors.text, hover: colors.hover, selected: colors.selected, disabled: colors.emptyStars, disabledBackground: colors.raised }
      },
      components: {
        MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
        MuiButtonBase: { styleOverrides: { root: { "&.Mui-focusVisible": { outline: "2px solid " + colors.focus, outlineOffset: 2 } } } },
        MuiTooltip: { styleOverrides: { tooltip: { color: colors.text, backgroundColor: colors.raised, border: "1px solid " + colors.border } } }
      }
    });
  }, [surface, outerTheme, inheritedMuiTheme]);
  return <NpsSurfaceContext.Provider value={surface}><MuiThemeProvider theme={muiTheme}><Dashboard /></MuiThemeProvider></NpsSurfaceContext.Provider>;
};

export default DashboardWithPalette;
